/* eslint-disable babel/camelcase -- posthog-js event property names are snake_case (external API) */
import { ORDER_TOKEN_PARAM } from '../redesign/services/checkoutStepUrl';

const TOKEN_BEARING_SEGMENTS = [
    'ticket-link',
    'order',
    'check-order',
    'handle_callback',
    'pdf',
    'ticket',
];
const LITERAL_SEGMENTS = [
    'recalculate-price',
    'refresh-session',
];
const TOKEN_BEARING_PARAMETERS = [ORDER_TOKEN_PARAM, 'hash'];
const REDACTED_SEGMENT = ':token';
const URL_SCHEME_SOURCE = '[a-z][a-z0-9+.-]{0,31}://';
const ORIGIN_PATTERN = new RegExp(`^${URL_SCHEME_SOURCE}[^/]*`, 'i');
const WHITESPACE_RUN_PATTERN = /(\s+)/;
const TOKEN_VALUE_PATTERN = /^[^&#\s"'<>)]*/;
const TOKEN_PARAMETER_PATTERN = new RegExp(
    `([?&](?:${TOKEN_BEARING_PARAMETERS.join('|')})=)[^&#\\s"'<>)]*`,
    'gi',
);
const HREF_ATTRIBUTE_PATTERN = /(href=")([^"]*)(")/g;
const ELEMENTS_CHAIN_PROPERTY = '$elements_chain';

const redactPath = path => path
    .split('/')
    .map((segment, index, segments) => (
        index > 0
        && segment !== ''
        && TOKEN_BEARING_SEGMENTS.includes(segments[index - 1])
        && !TOKEN_BEARING_SEGMENTS.includes(segment)
        && !LITERAL_SEGMENTS.includes(segment)
            ? segment.replace(TOKEN_VALUE_PATTERN, REDACTED_SEGMENT)
            : segment
    ))
    .join('/');

const redactQueryParameter = (pair) => {
    const separatorIndex = pair.indexOf('=');

    if (separatorIndex === -1 || !TOKEN_BEARING_PARAMETERS.includes(pair.slice(0, separatorIndex))) {
        return pair;
    }

    return `${pair.slice(0, separatorIndex)}=${pair.slice(separatorIndex + 1).replace(TOKEN_VALUE_PATTERN, REDACTED_SEGMENT)}`;
};

const redactQuery = (query) => {
    if (query.charAt(0) !== '?') {
        return query;
    }

    const fragmentIndex = query.indexOf('#');
    const fragment = fragmentIndex === -1 ? '' : query.slice(fragmentIndex);
    const search = fragmentIndex === -1 ? query.slice(1) : query.slice(1, fragmentIndex);

    return `?${search.split('&').map(redactQueryParameter).join('&')}${fragment}`;
};

const redactUrlSpan = (span) => {
    const separatorIndex = span.search(/[?#]/);
    const withoutQuery = separatorIndex === -1 ? span : span.slice(0, separatorIndex);
    const query = separatorIndex === -1 ? '' : span.slice(separatorIndex);
    const originMatch = withoutQuery.match(ORIGIN_PATTERN);
    const origin = originMatch === null ? '' : originMatch[0];

    return `${origin}${redactPath(withoutQuery.slice(origin.length))}${redactQuery(query)}`;
};

const redactUrl = (value) => {
    if (typeof value !== 'string' || value === '') {
        return value;
    }

    return value
        .split(WHITESPACE_RUN_PATTERN)
        .map(redactUrlSpan)
        .join('')
        .replace(TOKEN_PARAMETER_PATTERN, `$1${REDACTED_SEGMENT}`);
};

const redactObjectKeys = (value) => {
    const redacted = {};

    Object.keys(value).forEach((key) => {
        const redactedKey = redactUrl(key);
        const alreadyRedactedTo = redacted[redactedKey];

        redacted[redactedKey] = Array.isArray(alreadyRedactedTo) && Array.isArray(value[key])
            ? alreadyRedactedTo.concat(value[key])
            : value[key];
    });

    return redacted;
};

const isSeparatelyRedactedProperty = property => property === ELEMENTS_CHAIN_PROPERTY;

const sanitizePostHogProperties = (properties) => {
    const sanitized = { ...properties };

    Object.keys(sanitized)
        .filter(property => (
            typeof sanitized[property] === 'string' && !isSeparatelyRedactedProperty(property)
        ))
        .forEach((property) => {
            sanitized[property] = redactUrl(sanitized[property]);
        });

    if (Array.isArray(sanitized.$elements)) {
        sanitized.$elements = sanitized.$elements.map(element => (
            element !== null && typeof element === 'object' && element.attr__href !== undefined
                ? { ...element, attr__href: redactUrl(element.attr__href) }
                : element
        ));
    }

    if (typeof sanitized[ELEMENTS_CHAIN_PROPERTY] === 'string') {
        sanitized[ELEMENTS_CHAIN_PROPERTY] = sanitized[ELEMENTS_CHAIN_PROPERTY].replace(
            HREF_ATTRIBUTE_PATTERN,
            (match, prefix, url, suffix) => `${prefix}${redactUrl(url)}${suffix}`,
        );
    }

    if (sanitized.$heatmap_data !== null && typeof sanitized.$heatmap_data === 'object') {
        sanitized.$heatmap_data = redactObjectKeys(sanitized.$heatmap_data);
    }

    return sanitized;
};

export default sanitizePostHogProperties;
